import { useState, useEffect, useRef, useCallback } from 'react';

const TILE_SIZE = 32;
const GRID_WIDTH = 32;
const GRID_HEIGHT = 24;

const DigDugGame = ({ setgameshowing }) => {
    const canvasRef = useRef(null);
    const [gameState, setGameState] = useState('intro');
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(10000);
    const [lives, setLives] = useState(1);
    const [round, setRound] = useState(1);

    const gameRef = useRef({
        player: { x: 1, y: 1, dir: 'right', pumping: false, pumpTarget: null, pumpProgress: 0, dead: false, deathTimer: 0 },
        enemies: [],
        rocks: [],
        bonusItem: null,
        grid: [],
        keys: {},
        animFrame: 0,
        invincible: 0,
        enemiesDefeated: 0,
        rocksDropped: 0,
        lastEnemyEscaping: false
    });

    const initLevel = useCallback((roundNum) => {
        const grid = Array(GRID_HEIGHT).fill(null).map(() => Array(GRID_WIDTH).fill(1));

        // Top rows are empty (surface)
        for (let y = 0; y < 3; y++) {
            for (let x = 0; x < GRID_WIDTH; x++) {
                grid[y][x] = 0;
            }
        }

        // Generate enemies (more as rounds increase)
        const numEnemies = Math.min(4 + Math.floor(roundNum / 2), 8);
        const enemies = [];
        for (let i = 0; i < numEnemies; i++) {
            const isFygar = Math.random() > 0.5;
            const ex = 5 + Math.random() * 20;
            const ey = 5 + Math.random() * 15;
            enemies.push({
                x: ex,
                y: ey,
                type: isFygar ? 'fygar' : 'pooka',
                dir: ['left', 'right', 'up', 'down'][Math.floor(Math.random() * 4)],
                ghostMode: true, // Start in ghost mode since spawning in dirt
                stunned: 0,
                inflation: 0,
                fireTimer: 0,
                escaping: false,
                speed: 1.0,
                thinkTimer: 0
            });
        }

        // Place rocks
        const rocks = [];
        const numRocks = 6 + Math.floor(roundNum / 3);
        for (let i = 0; i < numRocks; i++) {
            const x = Math.floor(3 + Math.random() * (GRID_WIDTH - 6));
            const y = Math.floor(5 + Math.random() * 10);
            if (grid[y] && grid[y][x] === 1) {
                rocks.push({ x, y, falling: false, fallTimer: 0 });
                grid[y][x] = 2; // Mark as rock
            }
        }

        gameRef.current = {
            player: { x: 3, y: 2.7, dir: 'right', pumping: false, pumpTarget: null, pumpProgress: 0, dead: false, deathTimer: 0 },
            enemies,
            rocks,
            bonusItem: null,
            grid,
            keys: {},
            animFrame: 0,
            invincible: 60,
            enemiesDefeated: 0,
            rocksDropped: 0,
            lastEnemyEscaping: false
        };
    }, []);

    useEffect(() => {
        initLevel(round);
    }, [round, initLevel]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'Enter'].includes(e.key)) {
                e.preventDefault();
            }

            // Start game from intro screen
            if (gameState === 'intro' && e.key === 'Enter') {
                setGameState('playing');
                initLevel(1);
                return;
            }

            // Don't process Enter key during gameplay
            if (e.key === 'Enter' && gameState === 'playing') {
                return;
            }

            gameRef.current.keys[e.key] = true;
        };

        const handleKeyUp = (e) => {
            gameRef.current.keys[e.key] = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const drawGame = useCallback((ctx) => {
        const game = gameRef.current;
        const { player, enemies, rocks, grid, animFrame, bonusItem } = game;

        // Clear canvas
        ctx.fillStyle = '#000080';
        ctx.fillRect(0, 0, GRID_WIDTH * TILE_SIZE, GRID_HEIGHT * TILE_SIZE);

        // Draw grid
        for (let y = 0; y < GRID_HEIGHT; y++) {
            for (let x = 0; x < GRID_WIDTH; x++) {
                if (y < 3) {
                    // Surface - blue
                    ctx.fillStyle = '#000080';
                } else if (grid[y][x] === 0) {
                    // Dug tunnel - black
                    ctx.fillStyle = '#000000';
                } else if (grid[y][x] === 1) {
                    // Dirt - gradient orange/red
                    const depth = (y - 3) / (GRID_HEIGHT - 3);
                    const r = Math.floor(255 - depth * 100);
                    const g = Math.floor(140 - depth * 80);
                    ctx.fillStyle = `rgb(${r}, ${g}, 0)`;

                    // Add texture
                    ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
                    ctx.fillStyle = 'rgba(0,0,0,0.1)';
                    for (let i = 0; i < 3; i++) {
                        ctx.fillRect(
                            x * TILE_SIZE + Math.random() * TILE_SIZE,
                            y * TILE_SIZE + Math.random() * TILE_SIZE,
                            2, 2
                        );
                    }
                    continue;
                }
                ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
            }
        }

        // Draw rocks
        rocks.forEach(rock => {
            ctx.fillStyle = '#654321';
            ctx.fillRect(rock.x * TILE_SIZE + 2, rock.y * TILE_SIZE + 2, TILE_SIZE - 4, TILE_SIZE - 4);
            ctx.fillStyle = '#8B4513';
            ctx.fillRect(rock.x * TILE_SIZE + 4, rock.y * TILE_SIZE + 4, TILE_SIZE - 8, TILE_SIZE - 8);
        });

        // Draw bonus item
        if (bonusItem) {
            ctx.fillStyle = '#FFD700';
            ctx.beginPath();
            ctx.arc(bonusItem.x * TILE_SIZE + TILE_SIZE / 2, bonusItem.y * TILE_SIZE + TILE_SIZE / 2, 8, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw enemies
        enemies.forEach(enemy => {
            const ex = enemy.x * TILE_SIZE;
            const ey = enemy.y * TILE_SIZE;

            if (enemy.ghostMode) {
                // Ghost mode - just eyes
                ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                ctx.fillRect(ex + 8, ey + 10, 6, 6);
                ctx.fillRect(ex + 18, ey + 10, 6, 6);
                ctx.fillStyle = '#000';
                ctx.fillRect(ex + 10, ey + 12, 2, 2);
                ctx.fillRect(ex + 20, ey + 12, 2, 2);
            } else if (enemy.inflation > 0) {
                // Inflated enemy
                const size = TILE_SIZE + enemy.inflation * 10;
                ctx.fillStyle = enemy.type === 'pooka' ? '#FF6B6B' : '#4CAF50';
                ctx.beginPath();
                ctx.arc(ex + TILE_SIZE / 2, ey + TILE_SIZE / 2, size / 2, 0, Math.PI * 2);
                ctx.fill();
            } else {
                // Normal enemy
                if (enemy.type === 'pooka') {
                    // Red round body
                    ctx.fillStyle = '#FF3333';
                    ctx.beginPath();
                    ctx.arc(ex + TILE_SIZE / 2, ey + TILE_SIZE / 2, 14, 0, Math.PI * 2);
                    ctx.fill();

                    // Orange shading on body
                    ctx.fillStyle = '#FF6633';
                    ctx.beginPath();
                    ctx.arc(ex + TILE_SIZE / 2 - 3, ey + TILE_SIZE / 2 - 3, 10, 0, Math.PI * 2);
                    ctx.fill();

                    // Large white goggles
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(ex + 8, ey + 12, 8, 10);
                    ctx.fillRect(ex + 16, ey + 12, 8, 10);

                    // Black pupils
                    ctx.fillStyle = '#000000';
                    ctx.beginPath();
                    ctx.arc(ex + 12, ey + 17, 3, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.beginPath();
                    ctx.arc(ex + 20, ey + 17, 3, 0, Math.PI * 2);
                    ctx.fill();

                    // Goggle frame
                    ctx.strokeStyle = '#FFCC00';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(ex + 8, ey + 12, 8, 10);
                    ctx.strokeRect(ex + 16, ey + 12, 8, 10);

                    // Antenna/horn on top
                    ctx.fillStyle = '#FF6633';
                    ctx.fillRect(ex + 14, ey + 6, 4, 6);
                } else {
                    // Fygar - dragon with better rendering
                    // Body - purple/blue dragon
                    ctx.fillStyle = '#9C27B0';
                    ctx.fillRect(ex + 6, ey + 12, 20, 14);

                    // Darker purple for depth
                    ctx.fillStyle = '#6A1B9A';
                    ctx.fillRect(ex + 8, ey + 14, 16, 10);

                    // Head - more square/reptilian
                    ctx.fillStyle = '#9C27B0';
                    ctx.fillRect(ex + 12, ey + 8, 8, 12);

                    // Eyes - yellow with red pupils
                    ctx.fillStyle = '#FFEB3B';
                    ctx.fillRect(ex + 13, ey + 10, 3, 4);
                    ctx.fillRect(ex + 18, ey + 10, 3, 4);
                    ctx.fillStyle = '#D32F2F';
                    ctx.fillRect(ex + 14, ey + 11, 1, 2);
                    ctx.fillRect(ex + 19, ey + 11, 1, 2);

                    // Snout
                    ctx.fillStyle = '#7B1FA2';
                    ctx.fillRect(ex + 14, ey + 14, 4, 3);

                    // Spikes on back - orange/yellow
                    ctx.fillStyle = '#FF9800';
                    ctx.fillRect(ex + 8, ey + 6, 4, 7);
                    ctx.fillRect(ex + 14, ey + 4, 4, 9);
                    ctx.fillRect(ex + 20, ey + 6, 4, 7);

                    // Wings (small)
                    ctx.fillStyle = '#AB47BC';
                    ctx.fillRect(ex + 4, ey + 16, 4, 6);
                    ctx.fillRect(ex + 24, ey + 16, 4, 6);

                    // Fire
                    if (enemy.fireTimer > 0) {
                        const fireDir = enemy.dir === 'right' ? 1 : enemy.dir === 'left' ? -1 : 0;
                        if (fireDir !== 0) {
                            ctx.fillStyle = '#FF4500';
                            const fx = fireDir > 0 ? ex + TILE_SIZE : ex - TILE_SIZE;
                            ctx.fillRect(fx, ey + 12, TILE_SIZE, 10);
                            ctx.fillStyle = '#FFA500';
                            ctx.fillRect(fx + (fireDir > 0 ? 4 : -4), ey + 14, TILE_SIZE - 8, 6);
                            ctx.fillStyle = '#FFFF00';
                            ctx.fillRect(fx + (fireDir > 0 ? 8 : -8), ey + 16, TILE_SIZE - 16, 2);
                        }
                    }
                }
            }
        });

        // Draw player
        const px = player.x * TILE_SIZE;
        const py = player.y * TILE_SIZE;

        if (player.dead) {
            // Draw explosion
            const explosionSize = Math.min(player.deathTimer * 2, 40);
            const colors = ['#FF0000', '#FF8800', '#FFFF00', '#FF0000'];
            const colorIndex = Math.floor(player.deathTimer / 5) % colors.length;

            ctx.fillStyle = colors[colorIndex];
            ctx.beginPath();
            ctx.arc(px + TILE_SIZE / 2, py + TILE_SIZE / 2, explosionSize / 2, 0, Math.PI * 2);
            ctx.fill();

            // Explosion rays
            for (let i = 0; i < 8; i++) {
                const angle = (i / 8) * Math.PI * 2 + player.deathTimer * 0.1;
                const rayLength = explosionSize / 2 + 10;
                ctx.fillStyle = '#FFFF00';
                ctx.beginPath();
                ctx.moveTo(px + TILE_SIZE / 2, py + TILE_SIZE / 2);
                ctx.lineTo(
                    px + TILE_SIZE / 2 + Math.cos(angle) * rayLength,
                    py + TILE_SIZE / 2 + Math.sin(angle) * rayLength
                );
                ctx.lineWidth = 3;
                ctx.strokeStyle = colors[colorIndex];
                ctx.stroke();
            }
        } else {
            // Body - dress/skirt
            ctx.fillStyle = '#FF69B4';
            ctx.beginPath();
            ctx.moveTo(px + 16, py + 14);
            ctx.lineTo(px + 10, py + 24);
            ctx.lineTo(px + 22, py + 24);
            ctx.closePath();
            ctx.fill();

            // Head
            ctx.fillStyle = '#FFE5B4';
            ctx.beginPath();
            ctx.arc(px + 16, py + 12, 8, 0, Math.PI * 2);
            ctx.fill();

            // Hair - ponytail
            ctx.fillStyle = '#8B4513';
            ctx.beginPath();
            ctx.arc(px + 16, py + 10, 8, Math.PI, 2 * Math.PI);
            ctx.fill();

            // Ponytail
            ctx.fillStyle = '#8B4513';
            ctx.fillRect(px + 20, py + 8, 3, 8);
            ctx.beginPath();
            ctx.arc(px + 21.5, py + 16, 3, 0, Math.PI * 2);
            ctx.fill();

            // Eyes
            ctx.fillStyle = '#000000';
            ctx.fillRect(px + 13, py + 11, 2, 2);
            ctx.fillRect(px + 17, py + 11, 2, 2);

            // Smile
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(px + 16, py + 13, 3, 0, Math.PI);
            ctx.stroke();
        }

        // Pump
        if (player.pumping && player.pumpTarget && !player.dead) {
            const target = player.pumpTarget;
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(px + TILE_SIZE / 2, py + TILE_SIZE / 2);
            ctx.lineTo(target.x * TILE_SIZE + TILE_SIZE / 2, target.y * TILE_SIZE + TILE_SIZE / 2);
            ctx.stroke();
        }
    }, []);

    const updateGame = useCallback(() => {
        if (gameState !== 'playing') return;

        const game = gameRef.current;
        const { player, enemies, rocks, grid, keys } = game;

        game.animFrame++;
        if (game.invincible > 0) game.invincible--;

        // Handle death animation
        if (player.dead) {
            player.deathTimer++;
            if (player.deathTimer > 60) {
                // Check if game over or respawn
                if (lives <= 0) {
                    setGameState('gameover');
                } else {
                    // Respawn
                    player.dead = false;
                    player.deathTimer = 0;
                    player.x = 3;
                    player.y = 2.7;
                    game.invincible = 120;
                }
            }
            return; // Don't process other game logic during death
        }

        // Player movement
        if (!player.pumping) {
            let moved = false;
            let newX = player.x;
            let newY = player.y;

            if (keys.ArrowLeft) {
                player.dir = 'left';
                newX -= 0.1;
                moved = true;
            }
            if (keys.ArrowRight) {
                player.dir = 'right';
                newX += 0.1;
                moved = true;
            }
            if (keys.ArrowUp) {
                player.dir = 'up';
                newY -= 0.1;
                moved = true;
            }
            if (keys.ArrowDown) {
                player.dir = 'down';
                newY += 0.1;
                moved = true;
            }

            // Check bounds and collisions
            if (moved) {
                const nextTileX = Math.floor(newX);
                const nextTileY = Math.floor(newY);

                if (nextTileX >= 0 && nextTileX < GRID_WIDTH && nextTileY >= 0 && nextTileY < GRID_HEIGHT) {
                    // Can't move through rocks
                    if (grid[nextTileY] && grid[nextTileY][nextTileX] !== 2) {
                        player.x = newX;
                        player.y = newY;

                        // Dig tunnel at player's exact position
                        const playerTileX = Math.round(player.x);
                        const playerTileY = Math.round(player.y);

                        if (playerTileX >= 0 && playerTileX < GRID_WIDTH &&
                            playerTileY >= 0 && playerTileY < GRID_HEIGHT) {
                            if (grid[playerTileY][playerTileX] === 1) {
                                grid[playerTileY][playerTileX] = 0;

                                // Check rocks above
                                rocks.forEach(rock => {
                                    if (rock.x === playerTileX && rock.y === playerTileY - 1 && !rock.falling) {
                                        rock.fallTimer = 30;
                                    }
                                });
                            }
                        }
                    }
                }
            }
        }

        // Pump action
        if (keys[' '] && !player.pumping) {
            // Start pumping
            let targetX = player.x;
            let targetY = player.y;

            switch (player.dir) {
                case 'left': targetX -= 1; break;
                case 'right': targetX += 1; break;
                case 'up': targetY -= 1; break;
                case 'down': targetY += 1; break;
            }

            // Check if enemy is in range
            const target = enemies.find(e =>
                !e.ghostMode && e.inflation < 4 &&
                Math.abs(e.x - targetX) < 0.5 && Math.abs(e.y - targetY) < 0.5
            );

            if (target) {
                player.pumping = true;
                player.pumpTarget = target;
                player.pumpProgress = 0;
                target.stunned = 1;
            }
        }

        // Continue pumping
        if (player.pumping && player.pumpTarget) {
            if (keys[' ']) {
                player.pumpProgress++;
                if (player.pumpProgress % 15 === 0) {
                    player.pumpTarget.inflation++;

                    if (player.pumpTarget.inflation >= 4) {
                        // Enemy explodes
                        const idx = enemies.indexOf(player.pumpTarget);
                        if (idx > -1) {
                            enemies.splice(idx, 1);
                            game.enemiesDefeated++;
                            setScore(s => s + 100);
                        }
                        player.pumping = false;
                        player.pumpTarget = null;
                    }
                }
            } else {
                // Released pump
                player.pumping = false;
                if (player.pumpTarget) {
                    player.pumpTarget.stunned = 60;
                }
                player.pumpTarget = null;
            }
        }

        // Enemy deflation
        enemies.forEach(enemy => {
            if (enemy.stunned > 0) {
                enemy.stunned--;
                if (enemy.stunned === 0 && enemy.inflation > 0) {
                    enemy.inflation = Math.max(0, enemy.inflation - 0.1);
                }
            } else if (enemy.inflation > 0) {
                enemy.inflation = Math.max(0, enemy.inflation - 0.05);
            }
        });

        // Check if last enemy should escape
        if (enemies.length === 1 && game.enemiesDefeated > 0 && !game.lastEnemyEscaping) {
            game.lastEnemyEscaping = true;
            enemies[0].escaping = true;
        }

        // Enemy AI
        enemies.forEach(enemy => {
            if (enemy.stunned > 0 || enemy.inflation > 0) return;

            if (enemy.escaping) {
                // Move straight up
                if (enemy.y > 1) {
                    enemy.ghostMode = true;
                    enemy.y -= 0.05;
                } else {
                    // Reached surface, move to edge
                    enemy.ghostMode = false;
                    if (enemy.x < GRID_WIDTH / 2) {
                        enemy.x -= 0.1;
                    } else {
                        enemy.x += 0.1;
                    }

                    // Escaped
                    if (enemy.x < 0 || enemy.x > GRID_WIDTH) {
                        const idx = enemies.indexOf(enemy);
                        if (idx > -1) enemies.splice(idx, 1);
                    }
                }
                return;
            }

            const tileX = Math.floor(enemy.x);
            const tileY = Math.floor(enemy.y);

            // Check if in tunnel
            const inTunnel = grid[tileY] && grid[tileY][tileX] === 0;

            if (!inTunnel && !enemy.ghostMode) {
                // Enter ghost mode
                enemy.ghostMode = true;
            } else if (inTunnel && enemy.ghostMode) {
                // Exit ghost mode
                enemy.ghostMode = false;
            }

            // Decrease think timer
            if (enemy.thinkTimer > 0) {
                enemy.thinkTimer--;
            }

            // AI Decision making - make smarter decisions less frequently
            if (enemy.thinkTimer === 0) {
                enemy.thinkTimer = Math.floor(45 + Math.random() * 45); // Think every 0.75-1.5 seconds

                // Calculate distance to player
                const dx = player.x - enemy.x;
                const dy = player.y - enemy.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                // 90% of the time, chase the player aggressively
                if (Math.random() < 0.9 && distance > 0.5) {
                    // Choose direction based on largest distance component
                    const absDx = Math.abs(dx);
                    const absDy = Math.abs(dy);

                    // Try primary direction first (toward player on largest axis)
                    const primaryDir = absDx > absDy
                        ? (dx > 0 ? 'right' : 'left')
                        : (dy > 0 ? 'down' : 'up');

                    // Check if we can move in primary direction
                    let testX = enemy.x;
                    let testY = enemy.y;
                    const testDist = 0.5;

                    switch (primaryDir) {
                        case 'left': testX -= testDist; break;
                        case 'right': testX += testDist; break;
                        case 'up': testY -= testDist; break;
                        case 'down': testY += testDist; break;
                    }

                    const testTileX = Math.floor(testX);
                    const testTileY = Math.floor(testY);
                    const currentTileX = Math.floor(enemy.x);
                    const currentTileY = Math.floor(enemy.y);

                    // Check if primary direction is valid
                    let canUsePrimary = false;

                    if (enemy.ghostMode) {
                        // In ghost mode, can go anywhere except rocks
                        canUsePrimary = testTileX >= 0 && testTileX < GRID_WIDTH &&
                            testTileY >= 0 && testTileY < GRID_HEIGHT &&
                            grid[testTileY] && grid[testTileY][testTileX] !== 2;
                    } else {
                        // In tunnel mode, need open tunnel
                        canUsePrimary = testTileX >= 0 && testTileX < GRID_WIDTH &&
                            testTileY >= 0 && testTileY < GRID_HEIGHT &&
                            grid[testTileY] && (grid[testTileY][testTileX] === 0 ||
                                (testTileX === currentTileX && testTileY === currentTileY));
                    }

                    if (canUsePrimary) {
                        enemy.dir = primaryDir;
                    } else {
                        // Primary blocked, try secondary direction (other axis toward player)
                        const secondaryDir = absDx > absDy
                            ? (dy > 0 ? 'down' : 'up')
                            : (dx > 0 ? 'right' : 'left');
                        enemy.dir = secondaryDir;
                    }
                } else {
                    // 10% random movement for unpredictability
                    const possibleDirs = ['left', 'right', 'up', 'down'];
                    enemy.dir = possibleDirs[Math.floor(Math.random() * possibleDirs.length)];
                }
            }

            // Move with same speed as player
            const speed = enemy.ghostMode ? 0.05 : 0.1;

            let newX = enemy.x;
            let newY = enemy.y;

            switch (enemy.dir) {
                case 'left': newX -= speed; break;
                case 'right': newX += speed; break;
                case 'up': newY -= speed; break;
                case 'down': newY += speed; break;
            }

            // In ghost mode, can move anywhere except through rocks
            if (enemy.ghostMode) {
                const nextTileX = Math.floor(newX);
                const nextTileY = Math.floor(newY);

                // Check if trying to move into a rock
                if (nextTileX >= 0 && nextTileX < GRID_WIDTH &&
                    nextTileY >= 0 && nextTileY < GRID_HEIGHT &&
                    grid[nextTileY] && grid[nextTileY][nextTileX] === 2) {
                    // Hit a rock, try to go around it intelligently
                    const dx = player.x - enemy.x;
                    const dy = player.y - enemy.y;

                    // Try perpendicular directions
                    if (enemy.dir === 'left' || enemy.dir === 'right') {
                        enemy.dir = dy > 0 ? 'down' : 'up';
                    } else {
                        enemy.dir = dx > 0 ? 'right' : 'left';
                    }
                    enemy.thinkTimer = 30; // Don't rethink too soon
                } else {
                    enemy.x = newX;
                    enemy.y = newY;
                }
            } else {
                // In tunnel mode, check for walls and change direction if needed
                const nextTileX = Math.floor(newX);
                const nextTileY = Math.floor(newY);
                const currentTileX = Math.floor(enemy.x);
                const currentTileY = Math.floor(enemy.y);

                // Check if can move to new position (allow staying in current tile)
                if (nextTileX >= 0 && nextTileX < GRID_WIDTH &&
                    nextTileY >= 0 && nextTileY < GRID_HEIGHT &&
                    grid[nextTileY] && (grid[nextTileY][nextTileX] === 0 ||
                        (nextTileX === currentTileX && nextTileY === currentTileY))) {
                    enemy.x = newX;
                    enemy.y = newY;
                } else {
                    // Hit a wall, need to find valid direction
                    const dx = player.x - enemy.x;
                    const dy = player.y - enemy.y;

                    // Try all directions in order of preference
                    const directions = [
                        { dir: dx > 0 ? 'right' : 'left', priority: Math.abs(dx) },
                        { dir: dy > 0 ? 'down' : 'up', priority: Math.abs(dy) },
                        { dir: dx > 0 ? 'left' : 'right', priority: 0 },
                        { dir: dy > 0 ? 'up' : 'down', priority: 0 }
                    ].sort((a, b) => b.priority - a.priority);

                    let foundDir = false;
                    for (const { dir } of directions) {
                        let testX = enemy.x;
                        let testY = enemy.y;

                        switch (dir) {
                            case 'left': testX -= 0.3; break;
                            case 'right': testX += 0.3; break;
                            case 'up': testY -= 0.3; break;
                            case 'down': testY += 0.3; break;
                        }

                        const testTileX = Math.floor(testX);
                        const testTileY = Math.floor(testY);

                        if (testTileX >= 0 && testTileX < GRID_WIDTH &&
                            testTileY >= 0 && testTileY < GRID_HEIGHT &&
                            grid[testTileY] && grid[testTileY][testTileX] === 0) {
                            enemy.dir = dir;
                            foundDir = true;
                            break;
                        }
                    }

                    if (!foundDir) {
                        // If no valid direction found, enter ghost mode
                        enemy.ghostMode = true;
                    }

                    enemy.thinkTimer = 20; // Rethink soon
                }
            }

            // Bounds
            enemy.x = Math.max(0, Math.min(GRID_WIDTH - 1, enemy.x));
            enemy.y = Math.max(0, Math.min(GRID_HEIGHT - 1, enemy.y));

            // Fygar fire
            if (enemy.type === 'fygar' && !enemy.ghostMode && Math.random() < 0.01) {
                enemy.fireTimer = 30;
            }
            if (enemy.fireTimer > 0) {
                enemy.fireTimer--;

                // Check if player hit by fire
                const fireX = enemy.dir === 'right' ? enemy.x + 1 : enemy.x - 1;
                if (Math.abs(player.x - fireX) < 0.5 && Math.abs(player.y - enemy.y) < 0.5 && game.invincible === 0) {
                    player.dead = true;
                    player.deathTimer = 0;
                    setLives(l => l - 1);
                }
            }
        });

        // Rock physics
        rocks.forEach(rock => {
            if (rock.fallTimer > 0) {
                rock.fallTimer--;
                if (rock.fallTimer === 0) {
                    rock.falling = true;
                }
            }

            if (rock.falling) {
                rock.y += 0.15;

                const tileY = Math.floor(rock.y);

                // Check if landed
                if (tileY >= GRID_HEIGHT - 1 || (grid[tileY + 1] && grid[tileY + 1][rock.x] !== 0)) {
                    rock.falling = false;

                    // Remove rock
                    const idx = rocks.indexOf(rock);
                    if (idx > -1) {
                        rocks.splice(idx, 1);
                        game.rocksDropped++;

                        // Spawn bonus item after 2 rocks
                        if (game.rocksDropped === 2 && !game.bonusItem) {
                            game.bonusItem = {
                                x: Math.floor(Math.random() * (GRID_WIDTH - 4)) + 2,
                                y: Math.floor(Math.random() * (GRID_HEIGHT - 10)) + 5
                            };
                        }
                    }

                    // Check enemies crushed
                    let crushed = 0;
                    for (let i = enemies.length - 1; i >= 0; i--) {
                        const enemy = enemies[i];
                        if (Math.abs(enemy.x - rock.x) < 1 && Math.abs(enemy.y - tileY) < 1) {
                            enemies.splice(i, 1);
                            crushed++;
                            game.enemiesDefeated++;
                        }
                    }

                    if (crushed > 0) {
                        setScore(s => s + crushed * 500);
                    }

                    // Check player crushed
                    if (Math.abs(player.x - rock.x) < 0.5 && Math.abs(player.y - tileY) < 0.5 && game.invincible === 0) {
                        player.dead = true;
                        player.deathTimer = 0;
                        setLives(l => l - 1);
                    }
                }
            }
        });

        // Check bonus collection
        if (game.bonusItem) {
            if (Math.abs(player.x - game.bonusItem.x) < 0.5 && Math.abs(player.y - game.bonusItem.y) < 0.5) {
                setScore(s => s + 1000);
                game.bonusItem = null;
            }
        }

        // Check player-enemy collision
        if (game.invincible === 0 && !player.dead) {
            enemies.forEach(enemy => {
                if (!enemy.ghostMode && enemy.inflation === 0) {
                    if (Math.abs(player.x - enemy.x) < 0.5 && Math.abs(player.y - enemy.y) < 0.5) {
                        player.dead = true;
                        player.deathTimer = 0;
                        setLives(l => l - 1);
                    }
                }
            });
        }

        // Check round complete
        if (enemies.length === 0) {
            setRound(r => r + 1);
            setScore(s => s + 1000);
        }
    }, [gameState, lives]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        const gameLoop = setInterval(() => {
            updateGame();
            drawGame(ctx);
        }, 1000 / 60);

        return () => clearInterval(gameLoop);
    }, [updateGame, drawGame]);

    const restartGame = () => {
        setGameState('intro');
        setScore(0);
        setLives(1);
        setRound(1);
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
            {gameState === 'intro' ? (
                <div className="flex flex-col items-center justify-center bg-black p-12 rounded-lg border-4 border-yellow-500">
                    <div className="text-center mb-8">
                        <div className="text-7xl font-bold mb-2" style={{
                            background: 'linear-gradient(180deg, #FF1493 0%, #FF69B4 20%, #FFB6C1 40%, #FFFF00 60%, #FFD700 80%, #FFA500 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
                            fontFamily: 'Impact, sans-serif',
                            letterSpacing: '0.1em'
                        }}>
                            DIG DUG
                        </div>
                        <div className="text-2xl text-cyan-400 font-bold tracking-wider">
                            ONE HOUR CHALLENGE
                        </div>
                    </div>

                    <div className="mb-8 flex gap-16 items-center">
                        <div className="text-left space-y-2">
                            <div className="text-cyan-300 text-xl">6000</div>
                            <div className="text-pink-300 text-xl">7000</div>
                            <div className="text-yellow-300 text-xl">8000</div>
                        </div>

                        <div className="flex flex-col items-center gap-1">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="flex gap-1">
                                    <div className="w-6 h-3 bg-red-500 rounded-sm"></div>
                                    <div className="w-6 h-3 bg-pink-400 rounded-sm"></div>
                                    <div className="w-6 h-3 bg-yellow-400 rounded-sm"></div>
                                    <div className="w-6 h-3 bg-green-400 rounded-sm"></div>
                                </div>
                            ))}
                        </div>

                        <div className="text-right space-y-2">
                            <div className="text-cyan-300 text-xl">=400</div>
                            <div className="text-pink-300 text-xl">=600</div>
                            <div className="text-yellow-300 text-xl">=800</div>
                        </div>
                    </div>

                    <div className="text-red-500 text-2xl font-bold mb-4 animate-pulse">
                        PRESS ENTER TO START
                    </div>

                    <div className="text-gray-400 text-sm">
                        Arrow Keys: Move | Space: Pump
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-4 flex gap-8 text-white text-xl font-bold">
                        <div>SCORE: {score}</div>
                        <div className="text-red-500">HIGH SCORE: {highScore}</div>
                        <div>ROUND: {round}</div>
                        <div>LIVES: {lives}</div>
                    </div>

                    <canvas
                        ref={canvasRef}
                        width={GRID_WIDTH * TILE_SIZE}
                        height={GRID_HEIGHT * TILE_SIZE}
                        className="border-4 border-yellow-500"
                    />

                    <div className="mt-4 text-white text-center">
                        <div className="mb-2">Arrow Keys: Move | Space: Pump</div>
                        <div className="text-sm text-gray-400">
                            Inflate enemies to burst them or crush them with rocks!
                        </div>
                    </div>
                </>
            )}

            {gameState === 'gameover' && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-8 rounded-lg text-white text-center">
                        <div className="text-4xl font-bold mb-4">GAME OVER</div>
                        <div className="text-2xl mb-2">Final Score: {score}</div>
                        <button onClick={() => setgameshowing(false)} className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg text-5xl font-bold">
                            BACK TO MAIN SITE
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DigDugGame;